import { PlaceDetails } from '@/map/domain/entities/map.types';
import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { GoogleMapsService } from '@/map/application/services/googleMapsService';
import { LocationFormData } from './validationSchema';

const COORD_KEY_PRECISION = 5;

function coordKey(lat: number, lng: number): string {
  return `${Number(lat.toFixed(COORD_KEY_PRECISION))}-${Number(lng.toFixed(COORD_KEY_PRECISION))}`;
}

interface CountryPrefillFromGeocodeProps {
  setSelectedPlaceDetails: React.Dispatch<React.SetStateAction<PlaceDetails | null>>;
}

/**
 * Runs inside APIProvider so useMapsLibrary('geocoding') is available.
 * Reverse-geocodes lat/lng to pre-fill the country field when the backend doesn't provide it.
 */
export function CountryPrefillFromGeocode({ setSelectedPlaceDetails }: CountryPrefillFromGeocodeProps) {
  const formik = useFormikContext<LocationFormData>();
  const geocodingLib = useMapsLibrary('geocoding');
  const countryFetchedForCoordRef = useRef<string | null>(null);

  useEffect(() => {
    if (!geocodingLib) return;

    const { latitude, longitude, country } = formik.values;
    const hasCoordinates = latitude != null && longitude != null;
    const missingCountry = !country || String(country).trim() === '';
    const key = hasCoordinates ? coordKey(latitude, longitude) : null;
    const alreadyFetched = key !== null && countryFetchedForCoordRef.current === key;

    if (!hasCoordinates || !missingCountry || alreadyFetched) return;

    countryFetchedForCoordRef.current = key;

    const Geocoder = geocodingLib.Geocoder;
    const geocoder = new Geocoder();
    const googleMapsService = new GoogleMapsService();

    geocoder
      .geocode({ location: { lat: latitude, lng: longitude } })
      .then((result) => {
        if (!result.results[0]?.address_components?.length) return;
        const details = googleMapsService.extractAddressComponents(result.results[0].address_components);
        const fetchedCountry = details.country?.trim();
        if (fetchedCountry) {
          formik.setFieldValue('country', fetchedCountry);
          setSelectedPlaceDetails((prev) => (prev ? { ...prev, country: fetchedCountry } : null));
        }
      })
      .catch(() => {
        countryFetchedForCoordRef.current = null;
      });
  }, [geocodingLib, formik.values.latitude, formik.values.longitude, formik.values.country, formik.setFieldValue, setSelectedPlaceDetails]);

  return null;
}
